import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Lightbox tipo galería: pellizca, arrastra para mover, doble toque, rueda. */
export default function ImageZoomLightbox({ src, alt, onClose }) {
  const imgRef = useRef(null);
  const stateRef = useRef({ scale: 1, x: 0, y: 0 });
  const gestureRef = useRef(null);
  const [, bump] = useState(0);

  function applyTransform(animate) {
    const img = imgRef.current;
    if (!img) return;
    const { scale, x, y } = stateRef.current;
    img.style.transition = animate ? 'transform 0.2s ease' : 'none';
    img.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    img.style.cursor = scale > 1.02 ? 'grab' : 'zoom-in';
  }

  function setTransform({ scale, x, y }, animate = false) {
    const nextScale = Math.min(5, Math.max(1, scale));
    let nx = x;
    let ny = y;
    if (nextScale <= 1.02) {
      nx = 0;
      ny = 0;
    } else {
      const max = (nextScale - 1) * 280;
      nx = Math.max(-max, Math.min(max, nx));
      ny = Math.max(-max, Math.min(max, ny));
    }
    stateRef.current = { scale: nextScale, x: nx, y: ny };
    applyTransform(animate);
    bump((n) => n + 1);
  }

  useEffect(() => {
    applyTransform(false);
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function midTouch(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  function onTouchStart(e) {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      gestureRef.current = {
        type: 'pinch',
        dist,
        scale: stateRef.current.scale,
        x: stateRef.current.x,
        y: stateRef.current.y,
        mid: midTouch(e.touches),
      };
      return;
    }
    if (e.touches.length === 1) {
      const t = e.touches[0];
      gestureRef.current = {
        type: 'pan',
        x0: t.clientX,
        y0: t.clientY,
        ox: stateRef.current.x,
        oy: stateRef.current.y,
        scale: stateRef.current.scale,
        moved: false,
      };
    }
  }

  function onTouchMove(e) {
    const g = gestureRef.current;
    if (!g) return;
    if (e.touches.length === 2 && g.type === 'pinch') {
      if (e.cancelable) e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const ratio = dist / Math.max(1, g.dist);
      const mid = midTouch(e.touches);
      setTransform({
        scale: g.scale * ratio,
        x: g.x + (mid.x - g.mid.x),
        y: g.y + (mid.y - g.mid.y),
      });
      return;
    }
    if (e.touches.length === 1 && g.type === 'pan' && g.scale > 1.02) {
      if (e.cancelable) e.preventDefault();
      const t = e.touches[0];
      const dx = t.clientX - g.x0;
      const dy = t.clientY - g.y0;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) g.moved = true;
      setTransform({ scale: g.scale, x: g.ox + dx, y: g.oy + dy });
    }
  }

  function onTouchEnd() {
    gestureRef.current = null;
    if (stateRef.current.scale <= 1.02) {
      setTransform({ scale: 1, x: 0, y: 0 }, true);
    }
  }

  function onDoubleClick(e) {
    e.stopPropagation();
    e.preventDefault();
    const { scale } = stateRef.current;
    if (scale > 1.2) setTransform({ scale: 1, x: 0, y: 0 }, true);
    else setTransform({ scale: 2.5, x: 0, y: 0 }, true);
  }

  function onWheel(e) {
    e.preventDefault();
    const { scale, x, y } = stateRef.current;
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setTransform({ scale: scale + delta, x, y });
  }

  function onPointerDown(e) {
    if (e.pointerType === 'touch') return;
    if (stateRef.current.scale <= 1.02) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gestureRef.current = {
      type: 'mouse-pan',
      x0: e.clientX,
      y0: e.clientY,
      ox: stateRef.current.x,
      oy: stateRef.current.y,
      scale: stateRef.current.scale,
    };
  }

  function onPointerMove(e) {
    const g = gestureRef.current;
    if (!g || g.type !== 'mouse-pan') return;
    setTransform({
      scale: g.scale,
      x: g.ox + (e.clientX - g.x0),
      y: g.oy + (e.clientY - g.y0),
    });
  }

  function onPointerUp() {
    if (gestureRef.current?.type === 'mouse-pan') gestureRef.current = null;
  }

  const zoomed = stateRef.current.scale > 1.02;

  return createPortal(
    <div className="img-zoom-overlay" role="dialog" aria-modal="true">
      <button type="button" className="img-zoom-close" onClick={onClose} aria-label="Cerrar zoom">
        ✕
      </button>
      <p className="img-zoom-hint">
        {zoomed
          ? 'Arrastra para mover · pellizca para zoom · doble toque para reset'
          : 'Pellizca o doble toque para ampliar · arrastra cuando esté ampliada'}
      </p>
      <div
        className={`img-zoom-stage${zoomed ? ' is-zoomed' : ''}`}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onDoubleClick={onDoubleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img ref={imgRef} src={src} alt={alt || ''} className="img-zoom-img" draggable={false} />
      </div>
      <button type="button" className="img-zoom-backdrop-close" onClick={onClose} aria-label="Cerrar">
        Cerrar
      </button>
    </div>,
    document.body
  );
}
